import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

import {
  apiUrl,
  TENANT_ID,
  BUSINESS_UNIT_ID,
} from "@/config/api";

export type ApiSubCategory = {
  id?: string;
  name?: string;
  image?: string | null;
  description?: string | null;
};

export type ApiServiceCategory = {
  id?: string;
  name?: string;
  name_en?: string;
  image?: string | null;
  description?: string | null;
  subCategories?: ApiSubCategory[];
};

export type ApiService = {
  id?: string;
  categoryId?: string;
  name?: string;
  name_en?: string;
  image?: string | null;
  description?: string | null;
  totalPrice?: string;
  basePrice?: string;
  estimatedMinutes?: number;
  is_recommended?: number;
  benefits?: string | null;
  whatIsIncluded?: string | null;
  pricingType?: string;
  priceSymbol?: string;
};

type PaginatedResult<T> = {
  success?: boolean;
  result?: T[];
  total?: number;
  page?: number;
  limit?: number;
};

export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&rsquo;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractListItemsFromHtml(html: string | null | undefined): string[] {
  if (!html) return [];
  const items: string[] = [];
  const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const t = stripHtml(m[1]);
    if (t) items.push(t);
  }
  return items;
}

export function parseServicePrice(totalPrice: string | null | undefined): number {
  const n = parseFloat(String(totalPrice ?? "0").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export type CatalogService = {
  id: string;
  categoryId: string;
  categoryName: string;
  name: string;
  image: string;
  description: string;
  fullDescription: string;
  price: number;
  rating: number;
  reviews: number;
  duration: string;
  /** From single-service `specifications` when available */
  warranty?: string;
  /** From single-service `specifications` when available */
  serviceInterval?: string;
  /** From single-service `specifications.isCertifiedTechnicians` when true */
  certifiedTechnicians?: boolean;
  /** From single-service `specifications.isGenuineParts` when true */
  genuineParts?: boolean;
  features: string[];
  isRecommended: boolean;
  pricingType?: string;
  priceSymbol?: string;
  nameEn: string;
};

export type SingleRelatedService = {
  id: string;
  name: string;
  image: string;
  description: string;
  price: number;
  labourCharges: number;
  totalPrice: number;
  duration: string;
  isMostBooked: boolean;
  isRecommended: boolean;
  isCertifiedTechnicians: boolean;
  isGenuineParts: boolean;
  pricingType?: null | string;
  priceSymbol?: null | string;
  category: null | {
    id: string;
    name: string;
  };
  subCategory: null | {
    id: string;
    name: string;
  };
};

/** Normalized line from `get-single-service` `whatIsIncluded` / `benefits` locale objects. */
export type ServiceLineItem = {
  id: number;
  text: string;
};

export type WhenNeededLineItem = {
  icon?: string | null; 
  text?: string | null;
  imagePath?: string | null;
};

export type SingleCatalogService = {
  id: string;
  categoryId: string;
  categoryName: string;
  name: string;
  image: string;
  description: string;
  fullDescription: string;
  price: number;
  rating: number;
  reviews: number;
  duration: string;
  /** From single-service `specifications` when available */
  warranty?: string;
  /** From single-service `specifications` when available */
  serviceInterval?: string;
  /** From single-service `specifications.isCertifiedTechnicians` when true */
  certifiedTechnicians?: boolean;
  /** From single-service `specifications.isGenuineParts` when true */
  genuineParts?: boolean;
  whenNeeded?: ApiWhenNeededItem[];
  isRecommended: boolean;
  pricingType?: string;
  priceSymbol?: string;
  relatedServices?: SingleRelatedService[];
  /** `whatIsIncluded` + `benefits` API rows merged (single list for the UI). */
  benefits?: ServiceLineItem[];
};

const PLACEHOLDER_IMAGE = "/images/service-placeholder.png";

/** Same pattern as `productsApi` / `packagesApi` when the CS gateway expects `auth-key`. */
function workstationServicesHeaders(): Record<string, string> {
  const h: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-tenant-id": TENANT_ID,
    "x-business-unit-id": BUSINESS_UNIT_ID,
  };
  const authKey = process.env.CS_API_AUTH_KEY?.trim();
  if (authKey) h["auth-key"] = authKey;
  return h;
}

const TRANSIENT_UPSTREAM_HTTP = new Set([502, 503, 504]);
const WORKSTATION_POST_RETRIES = 3;

async function postWorkstationJson(
  path: string,
  body: unknown
): Promise<Response> {
  const url = apiUrl(path);
  const init: RequestInit = {
    method: "POST",
    headers: workstationServicesHeaders(),
    body: JSON.stringify(body),
    cache: "no-store",
  };
  let last: Response | undefined;
  for (let attempt = 1; attempt <= WORKSTATION_POST_RETRIES; attempt++) {
    const res = await fetch(url, init);
    last = res;
    const retry =
      TRANSIENT_UPSTREAM_HTTP.has(res.status) && attempt < WORKSTATION_POST_RETRIES;
    if (!retry) return res;
    await new Promise((r) => setTimeout(r, 250 * attempt));
  }
  return last!;
}

export function mapApiServiceToCatalog(
  s: ApiService,
  categoryNameById: Map<string, string>
): CatalogService | null {
  const id = s.id?.trim();
  if (!id) return null;
  const categoryId = s.categoryId?.trim() ?? "";
  const name = s.name?.trim() || s.name_en?.trim() || "Service";
  const image = s.image?.trim() || PLACEHOLDER_IMAGE;
  const descHtml = s.description ?? "";
  const description = stripHtml(descHtml).slice(0, 160) || name;
  const fullText = stripHtml(descHtml);
  const fullDescription = fullText || description;
  const price = parseServicePrice(s.totalPrice);
  const minutes = s.estimatedMinutes ?? 0;
  const duration = minutes != null && minutes > 0 ? formatMinutes(minutes) : "";
  const fromLists = [
    ...extractListItemsFromHtml(s.whatIsIncluded),
    ...extractListItemsFromHtml(s.benefits),
  ];
  const features =
    fromLists.length > 0
      ? fromLists.slice(0, 12)
      : minutes != null && minutes > 0
        ? [`Typical duration: ~${minutes} minutes`]
        : ["Professional service at Petromin workshops"];

  return {
    id,
    categoryId,
    categoryName: categoryNameById.get(categoryId) ?? "Services",
    name,
    image,
    description,
    fullDescription,
    price,
    rating: 4.8,
    reviews: 0,
    duration,
    features,
    isRecommended: s.is_recommended === 1,
    pricingType: s.pricingType,
    priceSymbol: s.priceSymbol,
    nameEn: s.name_en?.trim() ?? "",
  };
}

export async function fetchServiceCategoriesPage(body: {
  page: number;
  limit: number;
  langCode: "en" | "ar";
}): Promise<PaginatedResult<ApiServiceCategory> | null> {
  const res = await postWorkstationJson(
    "/workstations/get-all-service-category",
    body
  );
  if (!res.ok) return null;
  const data = (await res.json()) as PaginatedResult<ApiServiceCategory>;
  if (!Array.isArray(data.result)) return null;
  return {
    success: data.success !== false,
    result: data.result,
    total: data.total ?? 0,
    page: data.page ?? body.page,
    limit: data.limit ?? body.limit,
  };
}

export async function fetchServicesListPage(body: {
  page: number;
  limit: number;
  langCode: "en" | "ar";
  is_recommended?: boolean;
  most_booked_services?: boolean;
}): Promise<PaginatedResult<ApiService> | null> {
  const payload: Record<string, unknown> = {
    page: body.page,
    limit: body.limit,
    langCode: body.langCode,
  };
  if (typeof body.is_recommended === "boolean") {
    payload.is_recommended = body.is_recommended;
  }
  if (typeof body.most_booked_services === "boolean") {
    payload.most_booked_services = body.most_booked_services;
  }
  const res = await postWorkstationJson("/workstations/get-all-services", payload);
  if (!res.ok) return null;
  const data = (await res.json()) as PaginatedResult<ApiService>;
  if (!Array.isArray(data.result)) return null;
  return {
    success: data.success !== false,
    result: data.result,
    total: data.total ?? 0,
    page: data.page ?? body.page,
    limit: data.limit ?? body.limit,
  };
}

export async function fetchAllServiceCategories(
  langCode: "en" | "ar",
  pageSize = 50
): Promise<ApiServiceCategory[]> {
  const all: ApiServiceCategory[] = [];
  let page = 1;
  let total = Infinity;
  while (all.length < total && page < 100) {
    const chunk = await fetchServiceCategoriesPage({
      page,
      limit: pageSize,
      langCode,
    });
    if (!chunk?.result?.length) break;
    all.push(...chunk.result);
    total = chunk.total ?? all.length;
    if (chunk.result.length < pageSize) break;
    page += 1;
  }
  return all;
}

export async function fetchAllServices(
  langCode: "en" | "ar",
  pageSize = 100
): Promise<ApiService[]> {
  const all: ApiService[] = [];
  let page = 1;
  let total = Infinity;
  while (all.length < total && page < 100) {
    const chunk = await fetchServicesListPage({
      page,
      limit: pageSize,
      langCode,
    });
    if (!chunk?.result?.length) break;
    all.push(...chunk.result);
    total = chunk.total ?? all.length;
    if (chunk.result.length < pageSize) break;
    page += 1;
  }
  return all;
}

export function buildCategoryNameMap(
  categories: ApiServiceCategory[]
): Map<string, string> {
  const m = new Map<string, string>();
  for (const c of categories) {
    const id = c.id?.trim();
    const name = c.name?.trim() || c.name_en?.trim();
    if (id && name) m.set(id, name);
  }
  return m;
}

export function buildCatalog(
  raw: ApiService[],
  categories: ApiServiceCategory[]
): CatalogService[] {
  const nameMap = buildCategoryNameMap(categories);
  const out: CatalogService[] = [];
  for (const s of raw) {
    const row = mapApiServiceToCatalog(s, nameMap);
    if (row) out.push(row);
  }
  return out;
}

/** `get-single-service` response shape (partial). */
export type ApiSingleServicePrice = {
  actual?: string;
  original?: string;
  pricingType?: string;
  priceSymbol?: string;
};

export type ApiSingleServiceSpecifications = {
  warranty?: string;
  serviceDurationInMinutes?: number;
  serviceInterval?: string;
  isCertifiedTechnicians?: number | boolean;
  isGenuineParts?: number | boolean;
};

export type ApiSingleServiceImage = {
  url?: string;
  imagePath?: string | null;
};

export type ApiSingleServiceCategoryNested = {
  id?: string;
  name?: string;
  description?: string | null;
};

export type ApiWhenNeededItem = {
  icon?: string | null;
  text?: string | null;
  imagePath?: string | null;
};

export type ApiSingleServiceRelatedService = {
  id: string;
  basePrice: string;
  labourCharges: number;
  totalPrice: string;
  estimatedMinutes: number;
  image: string | null;
  isMostBooked: number;
  isRecommended: number;
  isCertifiedTechnicians: number;
  isGenuineParts: number;
  pricingType: string | null;
  priceSymbol: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
  };
  subCategory: {
    id: string;
    name: string;
  };
};

export type ApiSingleServiceResult = {
  id?: string;
  name?: string;
  description?: string | null;
  /** Some payloads use list-style `image` when `images` is empty. */
  image?: string | null;
  price?: ApiSingleServicePrice;
  specifications?: ApiSingleServiceSpecifications;
  images?: ApiSingleServiceImage[];
  category?: ApiSingleServiceCategoryNested;
  whenNeeded?: ApiWhenNeededItem[];
  benefits?: unknown;
  whatIsIncluded?: unknown;
  relatedServices?: ApiSingleServiceRelatedService[];
};

function parseSingleServiceDisplayPrice(
  p: ApiSingleServicePrice | undefined
): number {
  const actual = parseServicePrice(p?.actual);
  const original = parseServicePrice(p?.original);
  if (actual > 0) return actual;
  if (original > 0) return original;
  return actual || original;
}

/** Backend may send 1 / true / "1" / "true" / camelCase or snake_case keys. */
function specFlagTrue(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    return t === "1" || t === "true" || t === "yes";
  }
  return false;
}

function readTrustFlagFromObject(
  obj: unknown,
  keys: readonly string[]
): boolean {
  if (!obj || typeof obj !== "object") return false;
  const o = obj as Record<string, unknown>;
  for (const k of keys) {
    if (specFlagTrue(o[k])) return true;
  }
  return false;
}

const CERTIFIED_FLAG_KEYS = [
  "isCertifiedTechnicians",
  "is_certified_technicians",
  "certifiedTechnicians",
  "certified_technicians",
  "hasCertifiedTechnicians",
  "has_certified_technicians",
] as const;

const GENUINE_FLAG_KEYS = [
  "isGenuineParts",
  "is_genuine_parts",
  "genuineParts",
  "genuine_parts",
  "hasGenuineParts",
  "has_genuine_parts",
] as const;

function readCertifiedTechniciansFlag(r: ApiSingleServiceResult): boolean {
  return (
    readTrustFlagFromObject(r.specifications, CERTIFIED_FLAG_KEYS) ||
    readTrustFlagFromObject(r, CERTIFIED_FLAG_KEYS)
  );
}

function readGenuinePartsFlag(r: ApiSingleServiceResult): boolean {
  return (
    readTrustFlagFromObject(r.specifications, GENUINE_FLAG_KEYS) ||
    readTrustFlagFromObject(r, GENUINE_FLAG_KEYS)
  );
}

function pickFirstLocaleString(
  locale: unknown,
  ...keys: string[]
): string {
  if (!locale || typeof locale !== "object") return "";
  const o = locale as Record<string, unknown>;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return stripHtml(v).trim();
  }
  return "";
}

/**
 * `whatIsIncluded`: `[{ id, locale: { includedText } }, ...]`
 * Also accepts `locale.included_text` and legacy rows with top-level `text`.
 */
export function mapApiWhatsIncludedItems(value: unknown): ServiceLineItem[] {
  if (value == null) return [];
  if (typeof value === "string") return [];
  if (!Array.isArray(value)) return [];
  const out: ServiceLineItem[] = [];
  for (const raw of value) {
    if (raw == null) continue;
    if (typeof raw === "string") {
      const t = stripHtml(raw).trim();
      if (t) out.push({ id: out.length + 1, text: t });
      continue;
    }
    if (typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const idNum =
      typeof o.id === "number" && Number.isFinite(o.id)
        ? o.id
        : Number(o.id);
    let text = pickFirstLocaleString(
      o.locale,
      "includedText",
      "included_text",
      "text"
    );
    if (!text && typeof o.text === "string") {
      text = stripHtml(o.text).trim();
    }
    if (!text) continue;
    out.push({
      id: Number.isFinite(idNum) ? idNum : out.length + 1,
      text,
    });
  }
  return out;
}

/**
 * `benefits`: `[{ id, locale: { benefitText } }, ...]`
 * Also accepts `locale.benefit_text` and legacy `text` on the row.
 */
export function mapApiBenefitItems(value: unknown): ServiceLineItem[] {
  if (value == null) return [];
  if (typeof value === "string") return [];
  if (!Array.isArray(value)) return [];
  const out: ServiceLineItem[] = [];
  for (const raw of value) {
    if (raw == null) continue;
    if (typeof raw === "string") {
      const t = stripHtml(raw).trim();
      if (t) out.push({ id: out.length + 1, text: t });
      continue;
    }
    if (typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const idNum =
      typeof o.id === "number" && Number.isFinite(o.id)
        ? o.id
        : Number(o.id);
    let text = pickFirstLocaleString(
      o.locale,
      "benefitText",
      "benefit_text",
      "text"
    );
    if (!text && typeof o.text === "string") {
      text = stripHtml(o.text).trim();
    }
    if (!text) continue;
    out.push({
      id: Number.isFinite(idNum) ? idNum : out.length + 1,
      text,
    });
  }
  return out;
}

const formatMinutes = (mins: number) => {
  const dur = dayjs.duration(mins, "minutes");
  const hours = dur.hours();
  const minutes = dur.minutes();

  const h = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const m = minutes > 0 ? `${minutes} min${minutes > 1 ? "s" : ""}` : "";

  return `${h} ${m}`.trim();
};

export function mapSingleServiceResultToCatalog(
  r: ApiSingleServiceResult
): SingleCatalogService | null {
  const id = r.id?.trim();
  if (!id) return null;
  const name = r.name?.trim() || "Service";
  const imgUrl =
    r.images?.[0]?.url?.trim() ||
    (typeof r.image === "string" ? r.image.trim() : "");
  const image = imgUrl || PLACEHOLDER_IMAGE;
  const descHtml = r.description ?? "";
  const description = stripHtml(descHtml).slice(0, 160) || name;
  const fullText = stripHtml(descHtml);
  const fullDescription = fullText || description;
  const price = parseSingleServiceDisplayPrice(r.price);
  const minutes = r.specifications?.serviceDurationInMinutes;
  const duration = minutes != null && minutes > 0 ? formatMinutes(minutes) : "";
  const categoryId = r.category?.id?.trim() ?? "";
  const categoryName = r.category?.name?.trim() || "Services";
  const warranty = r.specifications?.warranty?.trim();
  const serviceInterval = r.specifications?.serviceInterval?.trim();
  const certifiedTechnicians = readCertifiedTechniciansFlag(r);
  const genuineParts = readGenuinePartsFlag(r);

  const whatsIncludedItems = mapApiWhatsIncludedItems(r.whatIsIncluded);
  const benefitItems = mapApiBenefitItems(r.benefits);
  const combinedBenefits: ServiceLineItem[] = [
    ...whatsIncludedItems,
    ...benefitItems,
  ].map((item, index) => ({
    id: index + 1,
    text: item.text,
  }));

  const normalizedRelatedServices = r?.relatedServices?.map((rs) => {
    const price = parseServicePrice(rs.totalPrice);
    const labourCharges = rs.labourCharges ?? 0;
    const totalFromApi = parseServicePrice(rs.totalPrice);
    const totalPrice =
      totalFromApi > 0 ? totalFromApi : price + labourCharges;
    return {
      id: rs.id,
      name: rs.name,
      image: rs.image ?? PLACEHOLDER_IMAGE,
      description: stripHtml(rs.description)?.slice(0, 160) || rs.name,
      price,
      labourCharges,
      totalPrice,
      duration: rs.estimatedMinutes != null && rs.estimatedMinutes > 0 ? formatMinutes(rs.estimatedMinutes) : "",
      isMostBooked: rs.isMostBooked === 1,
      isRecommended: rs.isRecommended === 1,
      isCertifiedTechnicians: rs.isCertifiedTechnicians === 1,
      isGenuineParts: rs.isGenuineParts === 1,
      pricingType: rs.pricingType ?? "",
      priceSymbol: rs.priceSymbol,
      category: {
        id: rs?.category?.id ?? "",
        name: rs?.category?.name ?? "",
      },
      subCategory: {
        id: rs?.subCategory?.id ?? "",
        name: rs?.subCategory?.name ?? "",
      },
    };
  }) ?? [];
  return {
    id,
    categoryId,
    categoryName,
    name,
    image,
    description,
    fullDescription,
    price,
    rating: 4.8,
    reviews: 0,
    duration,
    ...(warranty ? { warranty } : {}),
    ...(serviceInterval ? { serviceInterval } : {}),
    certifiedTechnicians,
    genuineParts,
    whenNeeded: r.whenNeeded ?? [],
    isRecommended: false,
    pricingType: r.price?.pricingType ?? "",
    priceSymbol: r.price?.priceSymbol ?? "",
    relatedServices: normalizedRelatedServices ?? [],
    benefits: combinedBenefits,
  };
}

export async function fetchGetSingleService(
  serviceId: string,
  langCode: "en" | "ar"
): Promise<ApiSingleServiceResult | null> {
  const id = serviceId.trim();
  if (!id) return null;
  const res = await postWorkstationJson(
    `/workstations/get-single-service/${encodeURIComponent(id)}`,
    { langCode }
  );
  if (!res.ok) return null;
  const data = (await res.json()) as {
    success?: boolean;
    result?: ApiSingleServiceResult;
    relatedServices?: ApiSingleServiceRelatedService[];
  };
  if (data.success === false || !data.result) return null;
  return {
    ...data.result,
    relatedServices: data?.relatedServices ?? [],
  };
}
